import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas';

const createInvoiceSignature = (value = '') => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return `sig_${hash.toString(16)}_${value.length}`;
};

const waitForInvoiceReady = async (invoiceRef, expectedSignature = '', timeoutMs = 10000) => {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const root = invoiceRef?.current;
    const isReady = root?.getAttribute('data-invoice-ready') === 'true';
    const rootSignature = root?.getAttribute('data-invoice-signature') || '';
    const isExpectedVersion = !expectedSignature || rootSignature === expectedSignature;

    if (isReady && isExpectedVersion) {
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 120));
  }

  throw new Error('Invoice template did not become ready in time for the requested invoice version');
};

const waitForLayoutStability = async () => {
  await new Promise((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise((resolve) => requestAnimationFrame(() => resolve()));
};

const getRowInkRatio = (rowData, width, stepX) => {
  let inkPixels = 0;
  let sampledPixels = 0;

  for (let x = 0; x < width; x += stepX) {
    const index = x * 4;
    const red = rowData[index];
    const green = rowData[index + 1];
    const blue = rowData[index + 2];
    const alpha = rowData[index + 3];
    const isWhitePixel = alpha < 10 || (red > 244 && green > 244 && blue > 244);

    if (!isWhitePixel) {
      inkPixels += 1;
    }
    sampledPixels += 1;
  }

  return inkPixels / Math.max(sampledPixels, 1);
};

const findNaturalBreakY = (canvas, fromY, defaultBreakY) => {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return defaultBreakY;
  }

  const searchWindowPx = 180;
  const rowStepPx = 2;
  const minSliceHeightPx = 340;

  const minCandidateY = Math.max(fromY + minSliceHeightPx, defaultBreakY - searchWindowPx);
  const maxCandidateY = Math.min(canvas.height - 1, defaultBreakY + searchWindowPx);
  if (minCandidateY >= maxCandidateY) {
    return defaultBreakY;
  }

  const sampleStepX = Math.max(1, Math.floor(canvas.width / 160));
  let bestY = defaultBreakY;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let y = minCandidateY; y <= maxCandidateY; y += rowStepPx) {
    const rowData = context.getImageData(0, y, canvas.width, 1).data;
    const inkRatio = getRowInkRatio(rowData, canvas.width, sampleStepX);
    const distancePenalty = (Math.abs(y - defaultBreakY) / searchWindowPx) * 0.08;
    const score = inkRatio + distancePenalty;

    if (score < bestScore) {
      bestScore = score;
      bestY = y;
    }
  }

  const maxAcceptableScore = 0.2;
  if (bestScore > maxAcceptableScore) {
    return defaultBreakY;
  }

  return bestY;
};

const addCanvasSliceToPage = async (pdfDoc, fullCanvas, fromY, sliceHeightPx, pageWidth, pageHeight, margin) => {
  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = fullCanvas.width;
  sliceCanvas.height = sliceHeightPx;

  const sliceCtx = sliceCanvas.getContext('2d');
  if (!sliceCtx) {
    throw new Error('Unable to create canvas context for PDF slice');
  }

  sliceCtx.fillStyle = '#ffffff';
  sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
  sliceCtx.drawImage(
    fullCanvas,
    0,
    fromY,
    fullCanvas.width,
    sliceHeightPx,
    0,
    0,
    sliceCanvas.width,
    sliceCanvas.height
  );

  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  const imgData = sliceCanvas.toDataURL('image/png');
  const imgBytes = await fetch(imgData).then((res) => res.arrayBuffer());
  const image = await pdfDoc.embedPng(imgBytes);

  const renderWidthPt = pageWidth - margin * 2;
  const pxPerPt = fullCanvas.width / renderWidthPt;
  const renderHeightPt = sliceHeightPx / pxPerPt;
  const y = pageHeight - margin - renderHeightPt;

  page.drawImage(image, {
    x: margin,
    y,
    width: renderWidthPt,
    height: renderHeightPt,
  });
};

export async function generateTransactionPDFFromHTML(invoiceRef, transactionId, expectedHtml = '') {
  try {
    if (!invoiceRef.current) {
      throw new Error("Invoice reference not found");
    }

    const expectedSignature = expectedHtml
      ? createInvoiceSignature(expectedHtml)
      : '';

    // Wait until InvoiceTemplate has fully processed incoming HTML.
    await waitForInvoiceReady(invoiceRef, expectedSignature, 15000);
    await waitForLayoutStability();

    // Capture the invoice component with print-safe options.
    const canvas = await html2canvas(invoiceRef.current, {
      scale: 3,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      width: invoiceRef.current.offsetWidth,
      height: invoiceRef.current.offsetHeight,
      scrollX: 0,
      scrollY: 0,
      logging: false,
      imageTimeout: 30000,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.textContent = `
          [data-invoice-root="true"],
          [data-invoice-root="true"] * {
            opacity: 1 !important;
            filter: none !important;
            transform: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color: inherit;
          }
        `;
        clonedDoc.head.appendChild(style);

        const clonedInvoice = clonedDoc.querySelector('[data-invoice-root="true"]');
        if (clonedInvoice) {
          clonedInvoice.style.background = '#ffffff';
          clonedInvoice.style.color = '#333333';
          clonedInvoice.style.opacity = '1';
          clonedInvoice.style.filter = 'none';
        }
      }
    });

    // Create PDF document with pagination instead of shrinking all content into one page.
    const pdfDoc = await PDFDocument.create();
    const pageWidth = 595.28; // A4 width in points
    const pageHeight = 841.89; // A4 height in points
    const margin = 22;
    const renderWidthPt = pageWidth - margin * 2;
    const renderHeightPt = pageHeight - margin * 2;

    const pxPerPt = canvas.width / renderWidthPt;
    const pageSliceHeightPx = Math.max(1, Math.floor(renderHeightPt * pxPerPt));

    let fromY = 0;
    while (fromY < canvas.height) {
      const remainingHeightPx = canvas.height - fromY;
      let sliceHeightPx = Math.min(pageSliceHeightPx, remainingHeightPx);

      if (remainingHeightPx > pageSliceHeightPx) {
        const defaultBreakY = fromY + sliceHeightPx;
        const naturalBreakY = findNaturalBreakY(canvas, fromY, defaultBreakY);
        sliceHeightPx = Math.max(1, naturalBreakY - fromY);
      }

      await addCanvasSliceToPage(
        pdfDoc,
        canvas,
        fromY,
        sliceHeightPx,
        pageWidth,
        pageHeight,
        margin
      );

      fromY += sliceHeightPx;
    }

    // Save and download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = `invoice_${transactionId || new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    
    return pdfBytes;
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    throw error;
  }
}
